import { escapeHtml } from "../utils/escapeHtml";
import { deadlineBadgeHtml } from "./DeadlineBadge";
import { formatNumber } from "../utils/formatting";

/* ── Pure helpers (exported for testing) ── */

export function computeDateDelta(parentDate: string, childDate: string): number {
  const p = new Date(parentDate);
  const c = new Date(childDate);
  if (isNaN(p.getTime()) || isNaN(c.getTime())) return Number.POSITIVE_INFINITY;
  return Math.round((c.getTime() - p.getTime()) / 86400000);
}

export function getDateInputClass(deltaDays: number): string {
  if (deltaDays >= 30) return "";
  if (deltaDays >= 20) return "date-warning";
  if (deltaDays >= 10) return "date-danger";
  return "date-critical";
}

export function defaultChildDate(parentDate: string): string {
  if (!parentDate) return "";
  const d = new Date(parentDate);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + 30);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/* ── Drawer state ── */

let activeBackdrop: HTMLElement | null = null;
let activeDrawer: HTMLElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

/* ── Public API ── */

export interface ValidationCountry {
  code: string;           // hub name, e.g. "France"
  volume: number;         // sum of assetVolume for this country's children
  defaultDate: string;    // YYYY-MM-DD, earliest child requestedDeliveryDate or parent+30d
  childIds: string[];     // child asset ids backing this country
}

export interface ValidationEntry {
  country: string;
  date: string;
  childIds: string[];
  volume: number;
}

export interface ValidationDrawerOpts {
  projectName: string;
  brand: string;
  division: string;
  type: string;
  parentDeliveryDate: string;
  assetCount: number;
  totalVolume: number;
  countries: ValidationCountry[];
  onValidate: (payload: {
    entries: ValidationEntry[];
    owner: string;
    notes: string;
  }) => Promise<void>;
  onClose: () => void;
}

export function openValidationDrawer(opts: ValidationDrawerOpts): void {
  closeValidationDrawer();

  const backdrop = document.createElement("div");
  backdrop.className = "validation-drawer-backdrop";
  backdrop.addEventListener("click", () => closeValidationDrawer());

  const drawer = document.createElement("div");
  drawer.className = "validation-drawer";

  const savedOwner = localStorage.getItem("ecf-last-owner") || "";
  const singleCountry = opts.countries.length === 1;

  const countriesMarkup = singleCountry
    ? renderSingleCountryRow(opts.countries[0], opts.parentDeliveryDate)
    : opts.countries
        .map((c) => renderCountryCheckboxRow(c, opts.parentDeliveryDate))
        .join("");

  drawer.innerHTML = `
    <button class="validation-drawer-close" type="button" aria-label="Close">&#x2715;</button>
    <div class="validation-drawer-header">
      <h2 class="validation-drawer-title">${escapeHtml(opts.projectName)}</h2>
      <div class="validation-drawer-subtitle">${escapeHtml(opts.brand)} &middot; ${escapeHtml(opts.division)} &middot; ${escapeHtml(opts.type)}</div>
    </div>
    <div class="validation-drawer-info">
      <div class="validation-drawer-info-card">
        <div class="validation-drawer-info-label">Parent delivery</div>
        <div class="validation-drawer-info-value">${escapeHtml(opts.parentDeliveryDate || "—")}</div>
        <div class="validation-drawer-info-sub">${deadlineBadgeHtml(opts.parentDeliveryDate)}</div>
      </div>
      <div class="validation-drawer-info-card">
        <div class="validation-drawer-info-label">Assets</div>
        <div class="validation-drawer-info-value">${escapeHtml(String(opts.assetCount))}</div>
      </div>
      <div class="validation-drawer-info-card">
        <div class="validation-drawer-info-label">Total volume</div>
        <div class="validation-drawer-info-value">${formatNumber(opts.totalVolume)}</div>
      </div>
    </div>
    <hr style="border:none;border-top:1px solid var(--line);margin:0.8rem 0;" />
    <h3 class="validation-drawer-section-title">Countries to validate</h3>
    <div class="validation-drawer-countries">
      ${countriesMarkup}
    </div>
    <div class="validation-drawer-form">
      <label>
        Owner
        <input class="input vd-owner" type="text" value="${escapeHtml(savedOwner)}" placeholder="Your name" />
      </label>
      <label>
        Notes
        <textarea class="input vd-notes" rows="3" placeholder="Optional notes"></textarea>
      </label>
      <div class="validation-drawer-actions">
        <button class="btn btn-primary vd-validate" type="button">Validate</button>
        <button class="btn btn-ghost vd-skip" type="button">Skip</button>
      </div>
    </div>
  `;

  // Per-country date color recalculation on input
  drawer.querySelectorAll<HTMLInputElement>(".vd-country-date").forEach((input) => {
    input.addEventListener("input", () => {
      const delta = computeDateDelta(opts.parentDeliveryDate, input.value);
      input.className = `input vd-country-date ${getDateInputClass(delta)}`.trim();
    });
  });

  const updateCta = () => {
    const checked = drawer.querySelectorAll<HTMLInputElement>(
      ".vd-country-check:checked",
    ).length;
    const cta = drawer.querySelector<HTMLButtonElement>(".vd-validate")!;
    if (singleCountry) {
      cta.textContent = `Validate ${opts.countries[0].code}`;
      cta.disabled = false;
      return;
    }
    cta.textContent = `Validate (${checked} ${checked === 1 ? "country" : "countries"})`;
    cta.disabled = checked === 0;
  };

  drawer.querySelectorAll<HTMLInputElement>(".vd-country-check").forEach((cb) => {
    cb.addEventListener("change", updateCta);
  });

  drawer.querySelector(".vd-validate")!.addEventListener("click", async () => {
    const owner = drawer.querySelector<HTMLInputElement>(".vd-owner")!.value.trim();
    const notes = drawer.querySelector<HTMLTextAreaElement>(".vd-notes")!.value.trim();
    if (owner) localStorage.setItem("ecf-last-owner", owner);
    const entries: ValidationEntry[] = [];
    const rows = drawer.querySelectorAll<HTMLElement>(".vd-country-row");
    rows.forEach((row) => {
      const check = row.querySelector<HTMLInputElement>(".vd-country-check");
      if (check && !check.checked) return;
      const code = row.dataset.country || "";
      const date = row.querySelector<HTMLInputElement>(".vd-country-date")?.value || "";
      const volume = Number(row.dataset.volume || "0");
      const childIds = (row.dataset.children || "").split("|").filter(Boolean);
      if (!code || !date) return;
      entries.push({ country: code, date, childIds, volume });
    });
    if (entries.length === 0) return;
    await opts.onValidate({ entries, owner, notes });
    closeValidationDrawer();
  });

  drawer.querySelector(".vd-skip")!.addEventListener("click", () => {
    opts.onClose();
    closeValidationDrawer();
  });
  drawer.querySelector(".validation-drawer-close")!.addEventListener("click", () =>
    closeValidationDrawer(),
  );

  escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeValidationDrawer();
  };
  document.addEventListener("keydown", escHandler);

  document.body.appendChild(backdrop);
  document.body.appendChild(drawer);
  activeBackdrop = backdrop;
  activeDrawer = drawer;
  updateCta();
}

function renderSingleCountryRow(c: ValidationCountry, parentDate: string): string {
  const d = c.defaultDate || defaultChildDate(parentDate);
  const klass = getDateInputClass(computeDateDelta(parentDate, d));
  const childrenAttr = c.childIds.join("|");
  return `<div class="vd-country-row vd-country-row-locked"
      data-country="${escapeHtml(c.code)}"
      data-volume="${c.volume}"
      data-children="${escapeHtml(childrenAttr)}">
    <span class="vd-country-badge">${escapeHtml(c.code)}</span>
    <input class="input vd-country-date ${klass}" type="date" value="${escapeHtml(d)}" />
    <span class="vd-country-volume">${formatNumber(c.volume)}</span>
  </div>`;
}

function renderCountryCheckboxRow(c: ValidationCountry, parentDate: string): string {
  const d = c.defaultDate || defaultChildDate(parentDate);
  const klass = getDateInputClass(computeDateDelta(parentDate, d));
  const childrenAttr = c.childIds.join("|");
  return `<div class="vd-country-row"
      data-country="${escapeHtml(c.code)}"
      data-volume="${c.volume}"
      data-children="${escapeHtml(childrenAttr)}">
    <label class="vd-country-check-label">
      <input class="vd-country-check" type="checkbox" checked />
      <span class="vd-country-name">${escapeHtml(c.code)}</span>
    </label>
    <input class="input vd-country-date ${klass}" type="date" value="${escapeHtml(d)}" />
    <span class="vd-country-volume">${formatNumber(c.volume)}</span>
  </div>`;
}

export function closeValidationDrawer(): void {
  if (escHandler) {
    document.removeEventListener("keydown", escHandler);
    escHandler = null;
  }
  if (activeBackdrop) {
    activeBackdrop.remove();
    activeBackdrop = null;
  }
  if (activeDrawer) {
    activeDrawer.classList.add("closing");
    const d = activeDrawer;
    activeDrawer = null;
    setTimeout(() => d.remove(), 200);
  }
}
